import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { PaginatedResponse, getPhotoSrc } from '@/lib/api'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import DataTable from '@/components/DataTable'
import AdvancedFilters from '@/components/AdvancedFilters'
import FilteredEmptyState from '@/components/FilteredEmptyState'
import SlideOver from '@/components/SlideOver'
import UserSearchSelect from '@/components/UserSearchSelect'
import CommentsSection from '@/components/CommentsSection'
import ClapjSurveyForm from '@/pages/ClapjSurveyForm'
import SolarSurveyForm from '@/pages/SolarSurveyForm'
import { Plus, Search, AlertCircle, Clock, CheckCircle, MapPin, Edit3, UserCheck, ClipboardList, Sun, Upload, Trash2, Image, FileText, X, Paperclip, ChevronLeft, ChevronRight, Download, Bell, Loader2, FileDown } from 'lucide-react'
import AuditInfo from '@/components/AuditInfo'
import { formatDateOnly } from '@/lib/dateUtils'

interface SiteSurvey {
  id: number
  sr_number: string
  client_name: string
  client_id: number | null
  contact_number: string
  address: string
  map_link: string
  project_type: string
  category: string
  priority: string
  assigned_to: string
  reported_date: string
  status: string
  description: string
  resolution_notes: string
  created_by: number | null
  created_by_name?: string
  created_at: string
  updated_at?: string
  photos?: any[]
  documents?: any[]
}

const STATUSES = ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Cancelled']
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low']
const CATEGORIES = ['New Installation', 'Maintenance', 'Inspection', 'Consultation', 'Other']
const PROJECT_TYPES = ['CLAPJ', 'FMTPJ', 'ELVPJ']

function getSsFileSrc(file: any, surveyId: number): string {
  if (!file) return ''
  if (file.storage === 'sharepoint' && file.sharepoint_path) {
    const token = localStorage.getItem('token')
    const base = `/api/site-surveys/sharepoint-file?survey_id=${surveyId}&path=${encodeURIComponent(file.sharepoint_path)}`
    return token ? `${base}&token=${encodeURIComponent(token)}` : base
  }
  return file.url || ''
}

function PhotoLightbox({ photos, index, surveyId, onClose, onPrev, onNext }: {
  photos: any[]; index: number; surveyId: number; onClose: () => void; onPrev: () => void; onNext: () => void
}) {
  const photo = photos[index]
  const src = getSsFileSrc(photo, surveyId)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') onPrev()
      if (e.key === 'ArrowRight') onNext()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, onPrev, onNext])

  const handleDownload = async () => {
    try {
      const res = await fetch(src)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = photo.name || 'photo.jpg'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      window.open(src, '_blank')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <p className="text-white text-sm font-medium truncate max-w-xs">{photo.name}</p>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-xs">{index + 1} / {photos.length}</span>
          <button onClick={handleDownload} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Download">
            <Download size={16} />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Close">
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-16" onClick={e => e.stopPropagation()}>
        {photos.length > 1 && (
          <button onClick={onPrev} className="absolute left-3 p-2 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors">
            <ChevronLeft size={24} />
          </button>
        )}
        <img
          key={src}
          src={src}
          alt={photo.name}
          className="max-w-full max-h-full object-contain rounded-lg"
        />
        {photos.length > 1 && (
          <button onClick={onNext} className="absolute right-3 p-2 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors">
            <ChevronRight size={24} />
          </button>
        )}
      </div>
      <div className="h-4 flex-shrink-0" />
    </div>
  )
}

function SsPhotoItem({ p, surveyId, canUpload, onDelete, onOpen }: { p: any; surveyId: number; canUpload: boolean; onDelete: () => void; onOpen: () => void }) {
  const [errored, setErrored] = useState(false)
  return (
    <div className="relative group aspect-square">
      {errored ? (
        <div className="w-full h-full flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 gap-1 p-1">
          <AlertCircle size={18} className="text-red-400" />
          <p className="text-xs text-red-500 text-center leading-tight">Photo unavailable</p>
          {p.url && (
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-0.5">View file</a>
          )}
        </div>
      ) : (
        <img
          src={getSsFileSrc(p, surveyId)}
          alt={p.name}
          className="w-full h-full object-cover rounded-lg border border-slate-200 cursor-pointer"
          onError={() => setErrored(true)}
          onClick={onOpen}
        />
      )}
      {canUpload && (
        <button
          onClick={onDelete}
          className="absolute top-1 right-1 p-0.5 bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
          <X size={10} />
        </button>
      )}
      <p className="text-xs text-slate-500 truncate mt-0.5">{p.name}</p>
    </div>
  )
}

export default function SiteSurveys() {
  const queryClient = useQueryClient()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = user.role || ''
  const isAdminRole = userRole === 'super_admin' || userRole === 'admin'
  const isFinance = userRole === 'finance'
  const isHR = userRole === 'hr'
  const isEngineer = userRole === 'engineer' || userRole === 'technician' || userRole === 'procurement' || userRole === 'storekeeper'
  const { canDo } = useMyPermissions()
  const canCreateSS = canDo('sr_ss_create')
  const canEditSS = (survey: SiteSurvey) => {
    if (isAdminRole || canDo('sr_ss_edit_all')) return true
    if (isFinance || isHR) return false
    if (isEngineer) return survey.assigned_to === user.full_name || survey.created_by === user.id
    return true
  }
  const canDeleteSS = isAdminRole
  const canUpload = canDo('sr_ss_create')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [engineerFilter, setEngineerFilter] = useState('')
  const [projectTypeFilter, setProjectTypeFilter] = useState('')
  const [reportedFrom, setReportedFrom] = useState('')
  const [reportedTo, setReportedTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<SiteSurvey | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [showSurveyForm, setShowSurveyForm] = useState<'clapj' | 'solar' | null>(null)
  const [form, setForm] = useState<any>({})
  const [error, setError] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [docError, setDocError] = useState('')
  const [pendingSsNotify, setPendingSsNotify] = useState<{name: string, download_url: string}[]>([])
  const [isNotifyingSs, setIsNotifyingSs] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/site-surveys/${openId}`).then(r => {
        setSelected(r.data)
        setEditMode(false)
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  useEffect(() => {
    setPendingSsNotify([])
  }, [selected?.id])

  const { data: filterUsers = [] } = useQuery<{ id: number; full_name: string; role: string }[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const engineeringUsers = filterUsers.filter(user => ['chief_engineer', 'operation_manager', 'engineer', 'technician'].includes(user.role))
  const advancedSurveyParams = {
    assigned_to: engineerFilter || undefined, project_type: projectTypeFilter || undefined,
    reported_from: reportedFrom || undefined, reported_to: reportedTo || undefined,
    created_from: createdFrom || undefined, created_to: createdTo || undefined,
  }
  const advancedSurveyFilterCount = Object.values(advancedSurveyParams).filter(Boolean).length
  const resetAdvancedSurveyFilters = () => {
    setEngineerFilter(''); setProjectTypeFilter(''); setReportedFrom(''); setReportedTo(''); setCreatedFrom(''); setCreatedTo(''); setPage(1)
  }
  const resetAllSurveyFilters = () => {
    setSearch(''); setStatusFilter(''); setPriorityFilter(''); setCategoryFilter(''); resetAdvancedSurveyFilters()
  }
  useEffect(() => {
    setPage(1)
  }, [engineerFilter, projectTypeFilter, reportedFrom, reportedTo, createdFrom, createdTo])

  const { data, isLoading } = useQuery<PaginatedResponse<SiteSurvey>>({
    queryKey: ['surveys', search, statusFilter, priorityFilter, categoryFilter, advancedSurveyParams, page],
    queryFn: () => api.get('/site-surveys', { params: { search, status: statusFilter, priority: priorityFilter, category: categoryFilter, ...advancedSurveyParams, page, per_page: 25 } }).then(r => r.data),
  })

  const { data: metricsData } = useQuery({
    queryKey: ['survey-metrics'],
    queryFn: async () => {
      const [pending, scheduled, completed] = await Promise.all([
        api.get('/site-surveys', { params: { status: 'Pending', per_page: 1 } }),
        api.get('/site-surveys', { params: { status: 'Scheduled', per_page: 1 } }),
        api.get('/site-surveys', { params: { status: 'Completed', per_page: 1 } }),
      ])
      return { pending: pending.data.total, scheduled: scheduled.data.total, completed: completed.data.total }
    },
  })

  const createSurvey = useMutation({
    mutationFn: (data: any) => api.post('/site-surveys', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['surveys'] }); queryClient.invalidateQueries({ queryKey: ['survey-metrics'] }); setShowCreate(false); setForm({}); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create site survey') },
  })

  const updateSurvey = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/site-surveys/${id}`, data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['surveys'] }); queryClient.invalidateQueries({ queryKey: ['survey-metrics'] }); setSelected(res.data); setEditMode(false); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to update site survey') },
  })

  const deleteSurvey = useMutation({
    mutationFn: (id: number) => api.delete(`/site-surveys/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['surveys'] }); queryClient.invalidateQueries({ queryKey: ['survey-metrics'] }); setSelected(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to delete site survey') },
  })

  const deletePhoto = useMutation({
    mutationFn: ({ ssId, photoId }: { ssId: number; photoId: string }) => api.delete(`/site-surveys/${ssId}/photos/${photoId}`),
    onSuccess: (_res, vars) => {
      setSelected(prev => prev ? { ...prev, photos: (prev.photos || []).filter((p: any) => p.id !== vars.photoId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['surveys'] })
    },
  })

  const deleteDoc = useMutation({
    mutationFn: ({ ssId, docId }: { ssId: number; docId: string }) => api.delete(`/site-surveys/${ssId}/documents/${docId}`),
    onSuccess: (_res, vars) => {
      setSelected(prev => prev ? { ...prev, documents: (prev.documents || []).filter((d: any) => d.id !== vars.docId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['surveys'] })
    },
  })

  const quickStatus = (newStatus: string) => {
    if (selected) updateSurvey.mutate({ id: selected.id, status: newStatus })
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selected) return
    const files = Array.from(e.target.files)
    if (photoInputRef.current) photoInputRef.current.value = ''
    setUploadingPhoto(true)
    setPhotoError('')
    const newPending: {name: string, download_url: string}[] = []
    for (const file of files) {
      try {
        const fd = new FormData()
        fd.append('file', file)
        const res = await api.post(`/site-surveys/${selected.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        setSelected(prev => prev ? { ...prev, photos: [...(prev.photos || []), res.data.file] } : prev)
        queryClient.invalidateQueries({ queryKey: ['surveys'] })
        if (res.data?.file?.download_url) {
          newPending.push({ name: res.data.file.name || file.name, download_url: res.data.file.download_url })
        }
      } catch (err: any) {
        setPhotoError(err.response?.data?.detail || 'Failed to upload photo')
      }
    }
    setUploadingPhoto(false)
    if (newPending.length > 0) {
      setPendingSsNotify(prev => [...prev, ...newPending])
    }
  }

  const handleSsNotify = async () => {
    if (!selected || pendingSsNotify.length === 0) return
    setIsNotifyingSs(true)
    try {
      const res = await api.post(`/site-surveys/${selected.id}/photos/batch-notify`, {
        download_urls: pendingSsNotify.map(p => p.download_url).filter(Boolean),
        names: pendingSsNotify.map(p => p.name),
      })
      if (res.data?.notification_sent !== false) {
        setPendingSsNotify([])
      } else {
        setPhotoError('Notification could not be delivered. Please try again.')
      }
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to send notification')
    } finally {
      setIsNotifyingSs(false)
    }
  }

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selected) return
    setUploadingDoc(true)
    setDocError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/site-surveys/${selected.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setSelected(prev => prev ? { ...prev, documents: [...(prev.documents || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['surveys'] })
    } catch (err: any) {
      setDocError(err.response?.data?.detail || 'Failed to upload document')
    } finally {
      setUploadingDoc(false)
      if (docInputRef.current) docInputRef.current.value = ''
    }
  }

  const columns = [
    { key: 'sr_number', label: 'SS#', render: (s: SiteSurvey) => <span className="font-medium text-red-800">{s.sr_number}</span> },
    { key: 'client_name', label: 'Client', render: (s: SiteSurvey) => <span className="font-medium text-slate-900">{s.client_name}</span> },
    { key: 'category', label: 'Category' },
    { key: 'priority', label: 'Priority', render: (s: SiteSurvey) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.priority === 'Critical' ? 'bg-red-100 text-red-700' : s.priority === 'High' ? 'bg-orange-100 text-orange-700' : s.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{s.priority}</span>
    )},
    { key: 'assigned_to', label: 'Assigned' },
    { key: 'reported_date', label: 'Survey Date', render: (s: SiteSurvey) => s.reported_date || '—' },
    { key: 'status', label: 'Status', render: (s: SiteSurvey) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'Pending' ? 'bg-amber-100 text-amber-700' : s.status === 'Scheduled' ? 'bg-red-100 text-red-900' : s.status === 'In Progress' ? 'bg-purple-100 text-purple-700' : s.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{s.status}</span>
    )},
  ]

  const metrics = [
    { label: 'Pending', value: metricsData?.pending ?? 0, icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Scheduled', value: metricsData?.scheduled ?? 0, icon: Clock, color: 'text-red-800', bg: 'bg-red-50' },
    { label: 'Completed', value: metricsData?.completed ?? 0, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  ]

  const errorBanner = error ? (
    <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>
  ) : null

  const photos = selected?.photos || []
  const documents = selected?.documents || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-900">Site Survey Requests</h1>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/site-surveys', { params: { status: statusFilter, priority: priorityFilter, category: categoryFilter, search, ...advancedSurveyParams }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'site_surveys.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          {canCreateSS && (
            <button onClick={() => { setShowCreate(true); setForm({ status: 'Pending', priority: 'Medium', category: 'New Installation', project_type: 'CLAPJ' }); setError('') }}
              className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900">
              <Plus size={16} /> New Survey
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {metrics.map(m => (
          <div key={m.label} className={`${m.bg} rounded-xl p-4 border border-slate-200`}>
            <div className="flex items-center gap-3">
              <m.icon className={m.color} size={20} />
              <div>
                <div className="text-2xl font-bold text-slate-900">{m.value}</div>
                <div className="text-xs text-slate-500">{m.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input type="text" placeholder="Search surveys..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1) }} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Priority</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1) }} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="">All Category</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <AdvancedFilters activeCount={advancedSurveyFilterCount} onReset={resetAdvancedSurveyFilters} onApply={() => setPage(1)}>
            <label className="text-xs font-medium text-slate-600">Site engineer
              <select value={engineerFilter} onChange={e => setEngineerFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All site engineers</option>{engineeringUsers.map(user => <option key={user.id} value={user.full_name}>{user.full_name}</option>)}</select>
            </label>
            <label className="text-xs font-medium text-slate-600">Project type
              <select value={projectTypeFilter} onChange={e => setProjectTypeFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All project types</option>{PROJECT_TYPES.map(type => <option key={type}>{type}</option>)}</select>
            </label>
            <label className="text-xs font-medium text-slate-600">Survey date from<input type="date" value={reportedFrom} onChange={e => setReportedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Survey date to<input type="date" value={reportedTo} onChange={e => setReportedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Created from<input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
            <label className="text-xs font-medium text-slate-600">Created to<input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" /></label>
          </AdvancedFilters>
        </div>
        <DataTable columns={columns} data={data?.data || []} total={data?.total || 0}
          page={page} totalPages={data?.total_pages || 1} onPageChange={setPage}
          onRowClick={s => { setSelected(s); setEditMode(false); setError(''); setShowSurveyForm(null); setPendingSsNotify([]) }} selectedId={selected?.id}
          getRowId={s => s.id} loading={isLoading} />
        <FilteredEmptyState show={!isLoading && (data?.total || 0) === 0 && !!(search || statusFilter || priorityFilter || categoryFilter || advancedSurveyFilterCount)} onReset={resetAllSurveyFilters} />
      </div>

      <SlideOver open={!!selected} onClose={() => { setSelected(null); setEditMode(false); setShowSurveyForm(null); setError(''); setPendingSsNotify([]) }} title={selected?.sr_number || ''} wide>
        {selected && showSurveyForm === 'clapj' && (
          <ClapjSurveyForm survey={selected} onClose={() => setShowSurveyForm(null)} />
        )}
        {selected && showSurveyForm === 'solar' && (
          <SolarSurveyForm survey={selected} onClose={() => setShowSurveyForm(null)} />
        )}
        {selected && !editMode && !showSurveyForm && (
          <div className="space-y-5">
            {errorBanner}
            <div className="flex flex-wrap gap-2">
              {selected.status === 'Pending' && <button onClick={() => quickStatus('Scheduled')} className="px-3 py-1.5 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Schedule</button>}
              {(selected.status === 'Pending' || selected.status === 'Scheduled') && <button onClick={() => quickStatus('In Progress')} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Start</button>}
              {(selected.status === 'Scheduled' || selected.status === 'In Progress') && <button onClick={() => quickStatus('Completed')} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Complete</button>}
              {selected.status !== 'Cancelled' && selected.status !== 'Pending' && <button onClick={() => quickStatus('Cancelled')} className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-700">Cancel</button>}
              {(selected.status === 'Completed' || selected.status === 'Cancelled') && <button onClick={() => quickStatus('Pending')} className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700">Reopen</button>}
            </div>

            {/* Survey Forms */}
            <div className="border border-slate-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Survey Forms</p>
              <div className="flex gap-2 flex-wrap">
                {(selected.project_type === 'CLAPJ' || selected.project_type === 'ELVPJ') && (
                  <button onClick={() => setShowSurveyForm('clapj')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800">
                    <ClipboardList size={14} /> Electric Fence Survey
                  </button>
                )}
                <button onClick={() => setShowSurveyForm('solar')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                  <Sun size={14} /> Solar Survey
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[['Client', selected.client_name], ['Phone Number', selected.contact_number], ['Project Type', selected.project_type], ['Category', selected.category], ['Priority', selected.priority], ['Status', selected.status], ['Assigned To', selected.assigned_to], ['Survey Date', formatDateOnly(selected.reported_date)]].map(([l, v]) => (
                <div key={l as string} className="text-sm"><span className="text-xs text-slate-500 block">{l}</span><span className="font-medium text-slate-900">{v || '—'}</span></div>
              ))}
            </div>
            <AuditInfo
              createdByName={selected.created_by_name}
              createdAt={selected.created_at}
              updatedAt={selected.updated_at}
            />
            {selected.address && <div className="text-sm"><span className="text-xs text-slate-500 block">Address</span><p className="text-slate-700 mt-1">{selected.address}</p></div>}
            {selected.map_link && (
              <div className="text-sm">
                <span className="text-xs text-slate-500 block">GPS Map Location</span>
                <a href={selected.map_link} target="_blank" rel="noopener noreferrer" className="text-red-800 underline text-sm break-all">{selected.map_link}</a>
                <iframe src={selected.map_link.replace('/place/', '/embed/place/')} className="w-full h-40 mt-2 rounded-lg border" allowFullScreen loading="lazy" />
              </div>
            )}
            {selected.description && <div className="text-sm"><span className="text-xs text-slate-500 block">Description</span><p className="text-slate-700 mt-1">{selected.description}</p></div>}
            {selected.resolution_notes && <div className="text-sm"><span className="text-xs text-slate-500 block">Findings / Notes</span><p className="text-slate-700 mt-1 bg-green-50 p-3 rounded-lg">{selected.resolution_notes}</p></div>}

            {/* Photos */}
            <div className="border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Image size={12} /> Photos ({photos.length})</p>
                <div className="flex items-center gap-2">
                  {canUpload && (
                    <label className={`flex items-center gap-1.5 px-3 py-1 text-xs bg-red-800 text-white rounded-lg cursor-pointer hover:bg-red-900 ${uploadingPhoto ? 'opacity-60 cursor-not-allowed' : ''}`}>
                      {uploadingPhoto ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {uploadingPhoto ? 'Uploading...' : 'Upload'}
                      <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                    </label>
                  )}
                  {pendingSsNotify.length > 0 && (
                    <button type="button" onClick={handleSsNotify} disabled={isNotifyingSs}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50">
                      {isNotifyingSs ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                      Notify Team ({pendingSsNotify.length})
                    </button>
                  )}
                </div>
              </div>
              {photoError && <p className="text-xs text-red-600 mb-2">{photoError}</p>}
              {photos.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No photos uploaded yet</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((p: any, i: number) => (
                    <SsPhotoItem
                      key={p.id}
                      p={p}
                      surveyId={selected.id}
                      canUpload={canUpload}
                      onOpen={() => setLightboxIndex(i)}
                      onDelete={() => { if (confirm('Delete this photo?')) deletePhoto.mutate({ ssId: selected.id, photoId: p.id }) }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Documents */}
            <div className="border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Paperclip size={12} /> Documents ({documents.length})</p>
                {canUpload && (
                  <label className={`flex items-center gap-1.5 px-3 py-1 text-xs bg-slate-700 text-white rounded-lg cursor-pointer hover:bg-slate-800 ${uploadingDoc ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <Upload size={12} /> {uploadingDoc ? 'Uploading...' : 'Upload'}
                    <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                  </label>
                )}
              </div>
              {docError && <p className="text-xs text-red-600 mb-2">{docError}</p>}
              {documents.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No documents uploaded yet</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((d: any) => (
                    <div key={d.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <FileText size={14} className="text-slate-500 shrink-0" />
                      <a href={getSsFileSrc(d, selected.id)} target="_blank" rel="noopener noreferrer"
                        className="flex-1 text-xs text-red-800 underline truncate">{d.name}</a>
                      <span className="text-xs text-slate-400 shrink-0">{d.size ? `${(d.size / 1024).toFixed(0)}KB` : ''}</span>
                      {canUpload && (
                        <button onClick={() => { if (confirm('Delete this document?')) deleteDoc.mutate({ ssId: selected.id, docId: d.id }) }}
                          className="p-0.5 text-red-500 hover:text-red-700 shrink-0">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SSActivityTrail ssId={selected.id} />
            <CommentsSection entityType="site_survey" entityId={selected.id} />

            {(canEditSS(selected) || canDeleteSS) && (
              <div className="flex gap-2 pt-3 border-t">
                {canEditSS(selected) && <button onClick={() => { setEditMode(true); setForm(selected); setError('') }} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>}
                {canDeleteSS && <button onClick={() => { if (confirm('Delete this survey?')) deleteSurvey.mutate(selected.id) }} className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>}
              </div>
            )}
          </div>
        )}
        {selected && editMode && (
          <form onSubmit={e => { e.preventDefault(); const { id, ...rest } = form; updateSurvey.mutate({ id: selected.id, ...rest }) }} className="space-y-3">
            {errorBanner}
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Client Name *</label>
              <input value={form.client_name || ''} onChange={e => setForm({ ...form, client_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Phone Number *</label>
              <input value={form.contact_number || ''} onChange={e => setForm({ ...form, contact_number: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Address *</label>
              <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">GPS Map Location</label>
              <input value={form.map_link || ''} onChange={e => setForm({ ...form, map_link: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="Google Maps URL (optional)" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Project Type *</label>
              <select value={form.project_type || 'CLAPJ'} onChange={e => setForm({ ...form, project_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
            <UserSearchSelect label="Assigned To" value={form.assigned_to || ''} onChange={v => setForm({ ...form, assigned_to: v })} />
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
              <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
              <select value={form.priority || ''} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Status</label>
              <select value={form.status || ''} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
                {STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Survey Date</label>
              <input type="date" value={form.reported_date || ''} onChange={e => setForm({ ...form, reported_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-sm font-medium text-slate-700 block mb-1">Findings / Notes</label>
              <textarea value={form.resolution_notes || ''} onChange={e => setForm({ ...form, resolution_notes: e.target.value })} rows={3} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
            <div className="flex gap-2">
              <button type="submit" disabled={updateSurvey.isPending} className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
                {updateSurvey.isPending ? 'Saving...' : 'Save'}</button>
              <button type="button" onClick={() => { setEditMode(false); setError('') }} className="py-2 px-4 text-sm bg-slate-100 rounded-lg">Cancel</button>
            </div>
          </form>
        )}
      </SlideOver>

      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setError('') }} title="New Site Survey">
        <form onSubmit={e => { e.preventDefault(); createSurvey.mutate(form) }} className="space-y-3">
          {errorBanner}
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Client Name *</label>
            <input value={form.client_name || ''} onChange={e => setForm({ ...form, client_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Phone Number *</label>
            <input value={form.contact_number || ''} onChange={e => setForm({ ...form, contact_number: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Address *</label>
            <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">GPS Map Location</label>
            <input value={form.map_link || ''} onChange={e => setForm({ ...form, map_link: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="Google Maps URL (optional)" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Project Type *</label>
            <select value={form.project_type || 'CLAPJ'} onChange={e => setForm({ ...form, project_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
          <UserSearchSelect label="Assigned To" value={form.assigned_to || ''} onChange={v => setForm({ ...form, assigned_to: v })} />
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
            <select value={form.category || 'New Installation'} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
            <select value={form.priority || 'Medium'} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Survey Date</label>
            <input type="date" value={form.reported_date || ''} onChange={e => setForm({ ...form, reported_date: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} className="w-full px-3 py-2 text-sm border rounded-lg" /></div>
          <button type="submit" disabled={createSurvey.isPending} className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createSurvey.isPending ? 'Creating...' : 'Create Site Survey'}
          </button>
        </form>
      </SlideOver>

      {lightboxIndex !== null && selected && (selected.photos || []).length > 0 && (
        <PhotoLightbox
          photos={selected.photos || []}
          index={lightboxIndex}
          surveyId={selected.id}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex(i => i !== null ? (i - 1 + (selected.photos || []).length) % (selected.photos || []).length : 0)}
          onNext={() => setLightboxIndex(i => i !== null ? (i + 1) % (selected.photos || []).length : 0)}
        />
      )}
    </div>
  )
}

function SSActivityTrail({ ssId }: { ssId: number }) {
  const { data: activities } = useQuery<any[]>({
    queryKey: ['ss-activities', ssId],
    queryFn: () => api.get(`/site-surveys/${ssId}/activities`).then(r => r.data),
    enabled: !!ssId,
  })

  if (!activities || activities.length === 0) return null

  const getIcon = (type: string) => {
    if (type.includes('Created')) return <Plus size={12} className="text-green-600" />
    if (type.includes('Status')) return <UserCheck size={12} className="text-blue-600" />
    return <Edit3 size={12} className="text-slate-500" />
  }

  return (
    <div className="text-sm">
      <span className="text-xs text-slate-500 font-medium block mb-2">Activity Trail</span>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {activities.map((a: any) => (
          <div key={a.id} className="flex items-start gap-2 text-xs bg-slate-50 px-3 py-2 rounded-lg">
            <div className="mt-0.5">{getIcon(a.activity_type)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-700">{a.subject}</div>
              <div className="text-slate-400">{a.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
